import { action, makeObservable, observable } from 'mobx';
import { mapService } from 'config/ioc';
import { FULFILLED, PENDING, REJECTED } from 'constants/actions';
import { isEmpty } from 'lodash';
import type {
  Coordinates,
  GeoObject,
  GeoObjectFormValues,
  GeoObjectPoint,
  MeetingObjectFormValues,
} from 'types/map';

interface GeocoderData {
  metaDataProperty?: {
    GeocoderMetaData?: {
      Address?: {
        Components: Array<{ kind: string; name: string }>;
      };
    };
  };
}

const getMapDataPropertyValue = (data: GeocoderData, kind: string) => {
  return data?.metaDataProperty?.GeocoderMetaData?.Address?.Components.find(
    (item) => item.kind === kind,
  )?.name;
};

const createInitialValues = (): GeoObjectFormValues => ({
  name: '',
  type: [],
  generalContractor: [],
  distributionChannel: [{ id: '01', name: 'Опт' }],
  region: [],
  city: '',
  district: '',
  street: '',
  house: '',
  floorsCount: 0,
  longitude: '',
  latitude: '',
  startDate: null,
  finishDate: null,
  squareFloor: 0,
  squareRoof: 0,
  squareOuterWalls: 0,
  squareInnerWalls: 0,
  website: '',
  phasesNumber: 0,
  undergroundFloorsCount: 0,
});

const createInitialMeetingValues = (): MeetingObjectFormValues => ({
  customer: '',
  construction: '',
  task: '',
  comment: '',
  selectedPoint: {
    longitude: '',
    latitude: '',
  },
  realPoint: {
    longitude: '',
    latitude: '',
  },
});

class MapStore {
  fetchState?: string;

  newGeoObject = createInitialValues();

  newMeetingObject = createInitialMeetingValues();

  newGeoObjectList: GeoObjectPoint[] = [];

  lastCoords: Coordinates | null = null;

  constructor() {
    makeObservable(this, {
      fetchState: observable,
      newGeoObject: observable.ref,
      newMeetingObject: observable.ref,
      newGeoObjectList: observable.ref,
      lastCoords: observable.ref,
      setLastCoords: action.bound,
      saveGeoObjectData: action.bound,
      saveMeetingObjectData: action.bound,
      setEmptyGeoObjectData: action.bound,
      setEmptyMeetingObjectData: action.bound,
      resetGeoObjectData: action.bound,
      resetMeetingObjectData: action.bound,
      getGeoObject: action.bound,
      saveNewPoint: action.bound,
      saveNewMeetingPoint: action.bound,
    });
  }

  setLastCoords(coords: Coordinates) {
    this.lastCoords = coords;
  }

  saveGeoObjectData(formData: GeoObjectFormValues) {
    this.newGeoObject = formData;
  }

  saveMeetingObjectData(formData: MeetingObjectFormValues) {
    this.newMeetingObject = formData;
  }

  setEmptyGeoObjectData() {
    this.newGeoObject = createInitialValues();
  }

  setEmptyMeetingObjectData() {
    this.newMeetingObject = createInitialMeetingValues();
  }

  resetGeoObjectData() {
    this.newGeoObject = createInitialValues();
  }

  resetMeetingObjectData() {
    this.newMeetingObject = createInitialMeetingValues();
  }

  async getGeoObject(id: string | number) {
    this.fetchState = PENDING;
    try {
      const mappedData = (await mapService.getGeoObject(id)) as GeoObject;
      this.newGeoObject = {
        ...createInitialValues(),
        ...mappedData,
        ...mappedData.address,
        type: [mappedData.type],
        generalContractor: [mappedData.generalContractor],
        region: mappedData.address.region ? [mappedData.address.region] : [],
      };
      this.fetchState = FULFILLED;
    } catch {
      this.fetchState = REJECTED;
    }
  }

  saveNewPoint(data: GeocoderData, coords: Coordinates) {
    const coordinates = {
      latitude: coords[0],
      longitude: coords[1],
    };
    // TODO сап принимает региона в виде region: [id]
    const address = {
      region: !isEmpty(this.newGeoObject.address?.region)
        ? [this.newGeoObject.address.region]
        : [],
      city: getMapDataPropertyValue(data, 'locality') || '',
      district: getMapDataPropertyValue(data, 'district') || '',
      house: getMapDataPropertyValue(data, 'house') || '',
      street: getMapDataPropertyValue(data, 'street') || '',
    };
    this.newGeoObject = { ...this.newGeoObject, ...address, ...coordinates };
  }

  saveNewMeetingPoint(_data: GeocoderData, coords: Coordinates) {
    const coordinates = {
      latitude: coords[0],
      longitude: coords[1],
    };
    this.newMeetingObject = {
      ...this.newMeetingObject,
      selectedPoint: coordinates,
    };
  }
}

const mapStore = new MapStore();

export default mapStore;
